import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import { RootState } from 'app/root-reducer';
import { jobSeekersAPI } from "api";
import { getErrorMessage } from 'utils/error';
import { JobSeekerJobSeekersState, JobSeekerJobSeekersTabs, JobSeekerJobSeekersTableView, JobSeekerJobSeekersTableFilters, JobSeekerJobSeekersTableRequestFiltersData } from './types';
import { Paging, Tab } from 'api/types';

export const getJobSeekerJobSeekersTable = createAsyncThunk<TableDataResponse<JobSeekerJobSeekersTableView>, void, RejectWithMessage>(
  'job-seeker-job-seekers/get-job-seekers-table',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = { ...state.jobSeekerJobSeekers.requestData };
      const onBehalfOfOrganization = state.jobSeekerOnBehalfOfOrganization.onBehalfOfOrganization;
      if (onBehalfOfOrganization) {
        requestData.onBehalfOfOrganizationId = onBehalfOfOrganization?.value;
      }
      return await jobSeekersAPI.getJobSeekerJobSeekersTable(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getJobSeekerJobSeekersTabs = createAsyncThunk<Tab[], void, RejectWithMessage>(
  'job-seeker-job-seekers/get-job-seekers-tabs',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = { ...state.jobSeekerJobSeekers.requestData };
      const onBehalfOfOrganization = state.jobSeekerOnBehalfOfOrganization.onBehalfOfOrganization;
      if (onBehalfOfOrganization) {
        requestData.onBehalfOfOrganizationId = onBehalfOfOrganization?.value;
      }
      return jobSeekersAPI.getJobSeekerJobSeekersTabsData(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialFilters = {
  text: '',
  tab: JobSeekerJobSeekersTabs.All,
  location: null,
  fullAddress: '',
  radius: '',
  assignedPrograms: [],
}

const initialRequestData = {
  paging: {
    page: 1,
    pageSize: 20,
  },
  filters: {
    tab: JobSeekerJobSeekersTabs.All,
    location: null,
    fullAddress: '',
    radius: undefined,
    assignedPrograms: [],
  },
};

const initialState: JobSeekerJobSeekersState = {
  status: 'idle',
  requestData: initialRequestData,
  currentFilters: initialFilters,
  data: {
    filters: null,
    tableDataIsLoading: false,
    tabsIsLoading: false,
    tabs: [],
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
  },
  error: null,
};

const jobSeekerJobSeekersTableSlice = createSlice({
  name: 'jobSeeker-jobseekers',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setFilters: (state, action: PayloadAction<Partial<JobSeekerJobSeekersTableFilters>>) => {
      const payloadFilters = action.payload;
      const newRequestFilters: Partial<JobSeekerJobSeekersTableRequestFiltersData> = {
        ...payloadFilters,
        assignedPrograms: payloadFilters.assignedPrograms?.map((item) => item.value)
      };
      state.requestData.filters = { ...state.requestData.filters, ...newRequestFilters };
      state.requestData.paging = initialRequestData.paging;

      const newFilters = { ...state.currentFilters, ...action.payload };
      state.currentFilters = newFilters;
    },
    setTab: (state, action: PayloadAction<string>) => {
      state.currentFilters.tab = action.payload;
      state.requestData.filters.tab = action.payload;
    },
    resetRequestData: (state) => {
      state.currentFilters = initialFilters;
      state.requestData = { ...initialState.requestData };
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getJobSeekerJobSeekersTable.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getJobSeekerJobSeekersTable.fulfilled, (state, action: PayloadAction<TableDataResponse<JobSeekerJobSeekersTableView>>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getJobSeekerJobSeekersTable.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });

    builder.addCase(getJobSeekerJobSeekersTabs.pending, (state) => {
      state.data.tabsIsLoading = true;
    });
    builder.addCase(getJobSeekerJobSeekersTabs.fulfilled, (state, action: PayloadAction<Tab[]>) => {
      state.data.tabsIsLoading = false;
      state.data.tabs = action.payload;
    });
    builder.addCase(getJobSeekerJobSeekersTabs.rejected, (state) => {
      state.data.tabsIsLoading = false;
    });
  },
});

export const { setPage, setTab, setFilters, resetRequestData, resetState } = jobSeekerJobSeekersTableSlice.actions;
export const jobSeekerJobSeekersTableReducer = jobSeekerJobSeekersTableSlice.reducer;
